import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import MaterialBackDrop from "../../components/ui/loading/materialBackDrop";
import UpdateUser from "../manage/updateUser/updateUser";
import {
  getAdminDuplicates,
  getUserById,
  adminSetPaymentHold,
} from "../../store/actions/generic";
import {
  GET_ADMIN_DUPLICATES,
  GET_USER_BY_ID,
  ADMIN_SET_PAYMENT_HOLD,
} from "../../store/actionTypes";

const PAYMENT_TYPE_TEXTS = {
  individual: "Bireysel",
  coach_bulk: "Öğretmen toplu",
  admin: "Admin",
};

const formatDateTime = (isoString) =>
  isoString
    ? new Date(isoString).toLocaleString("tr-TR", {
        timeZone: "Europe/Istanbul",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

const formatDate = (dateString) =>
  dateString ? dateString.split("-").reverse().join(".") : "-";

const paymentText = (u) => {
  if (!u.is_paid) return "Ödenmedi";
  const type =
    u.payment_type === "admin" && u.admin_name
      ? "Admin (" + u.admin_name + ")"
      : PAYMENT_TYPE_TEXTS[u.payment_type];
  return "Ödendi - " + type;
};

// users.payment_hold: null = otomatik kural, 1 = admin kapattı, 0 = admin açtı (backend: DuplicateUserManager.payment_blocks)
const paymentStatusText = (u) => {
  if (u.payment_hold === 1) return "Kapalı (admin: " + u.payment_hold_by + ")";
  if (u.payment_hold === 0) return "Açık (admin: " + u.payment_hold_by + ")";
  if (u.payment_block)
    return (
      "Kapalı (ödenmiş hesap: " +
      u.payment_block.paid_accounts.map((a) => "ID " + a.id).join(", ") +
      ")"
    );
  return "Açık";
};

const isWithinLastDay = (isoString) =>
  isoString && Date.now() - new Date(isoString).getTime() < 24 * 60 * 60 * 1000;

const cellStyle = { fontWeight: "bold" };

// Grupları türüne göre süzmek için. Bir grup birden fazla şekilde eşleşmişse (match_types) birden fazla kategoride görünür.
const CATEGORIES = [
  { key: "all", label: "Tümü", test: () => true },
  {
    key: "school_class",
    label: "Aynı okul ve sınıf",
    test: (g) => (g.match_types || []).includes("school_class"),
  },
  {
    key: "phone",
    label: "Aynı telefon",
    test: (g) => (g.match_types || []).includes("phone"),
  },
  {
    key: "grade_up",
    label: "Sınıf atlamış (aynı okul)",
    test: (g) => (g.match_types || []).includes("grade_up"),
  },
  {
    key: "school",
    label: "Aynı okul, sınıf farklı",
    test: (g) => (g.match_types || []).includes("school"),
  },
  {
    key: "double_paid",
    label: "Birden fazla hesabı ödenmiş",
    test: (g) => g.paid_count > 1,
  },
  {
    key: "blocked",
    label: "Ödemesi kapalı hesap var",
    test: (g) => g.users.some((u) => u.payment_hold === 1 || u.payment_block),
  },
  {
    key: "new",
    label: "Son 24 saat",
    test: (g) => isWithinLastDay(g.latest_created_at),
  },
];

// Aynı ad soyad + okul + sınıfla (ör. annesi bir, babası bir hesap açmış) ya da aynı ad soyad + telefonla
// (ör. sınıf atlayınca/okul değiştirince yeni hesap açmış) açılmış birden fazla öğrenci hesabı.
// Fazla hesabı silmek için isme tıklayıp açılan düzenleme penceresindeki silme butonu kullanılır.
// Silmek yerine hesabın ödemesi kapatılabilir; ödenmiş başka hesabı olan hesapta ödeme zaten otomatik kapalıdır.
const Duplicates = () => {
  const dispatch = useDispatch();
  const loading = useSelector(
    (state) =>
      state.loading[GET_ADMIN_DUPLICATES] ||
      state.loading[GET_USER_BY_ID] ||
      state.loading[ADMIN_SET_PAYMENT_HOLD]
  );
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [category, setCategory] = useState("all");

  const load = useCallback(() => {
    dispatch(getAdminDuplicates()).then((data) => {
      if (data && data.groups) {
        setGroups(data.groups);
        setError("");
      } else setError((data && data.message) || "Liste alınamadı.");
    });
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  // Düzenleme penceresi tam kullanıcı verisini (paid_receipts, okul öğretmenleri) bekliyor.
  const openUser = (id) => {
    dispatch(getUserById(id)).then((user) => {
      if (user && user.id) setSelectedUser(user);
      else alert("Kullanıcı bilgileri alınamadı. Silinmiş olabilir.");
    });
  };

  const setPaymentHold = (u, hold) => {
    dispatch(adminSetPaymentHold(u.id, hold)).then((res) => {
      if (res && res.error) alert(res.message);
      else if (res && res.message && res.message !== "Kaydedildi.")
        alert(res.message);
      load();
    });
  };

  const newCount = groups
    ? groups.filter((g) => isWithinLastDay(g.latest_created_at)).length
    : 0;
  const doublePaidCount = groups
    ? groups.filter((g) => g.paid_count > 1).length
    : 0;
  const activeCategory = CATEGORIES.find((c) => c.key === category);
  const visibleGroups = groups ? groups.filter(activeCategory.test) : null;

  return (
    <div style={{ color: "white" }}>
      {loading && <MaterialBackDrop />}
      <h2>Olası Çift Kayıtlar</h2>
      <p>
        Ad soyad, okul ve sınıfı aynı olan, ad soyad ve telefonu aynı olan
        (sınıf atlamış/okul değiştirmiş olabilir) ya da aynı okulda önceki
        sezon bir alt sınıfta açılmış öğrenci hesapları listelenir (doğum
        tarihi farklı olabilir, satırlarda gösterilir; Türkçe karakter ve büyük/küçük harf farkı dikkate alınmaz). Sadece bu sezon
        aktif hesabı olan gruplar gösterilir. Fazla hesabı silmek için isme
        tıklayın. Ödenmiş hesap silinemez. Ödenmiş başka hesabı olan hesapta
        ödeme otomatik kapanır; diğer hesapların ödemesini buradan elle
        kapatıp açabilirsiniz.
      </p>
      {error && <p style={{ color: "#ff6b6b" }}>{error}</p>}
      {groups && (
        <p>
          Toplam <b>{groups.length}</b> grup · Son 24 saatte oluşan:{" "}
          <b>{newCount}</b> · Birden fazla hesabı ödenmiş:{" "}
          <b style={{ color: doublePaidCount > 0 ? "#ff6b6b" : undefined }}>
            {doublePaidCount}
          </b>{" "}
          <Button
            variant="outlined"
            size="small"
            style={{ color: "white", borderColor: "white" }}
            onClick={load}
          >
            Yenile
          </Button>
        </p>
      )}
      {groups && groups.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
          {CATEGORIES.map((c) => {
            const count = groups.filter(c.test).length;
            const selected = c.key === category;
            return (
              <Button
                key={c.key}
                variant={selected ? "contained" : "outlined"}
                size="small"
                style={
                  selected
                    ? undefined
                    : { color: "white", borderColor: "white" }
                }
                color={selected ? "primary" : "default"}
                disabled={count === 0 && !selected}
                onClick={() => setCategory(c.key)}
              >
                {c.label} ({count})
              </Button>
            );
          })}
        </div>
      )}
      {groups && groups.length === 0 && <p>Çift kayıt bulunamadı.</p>}
      {visibleGroups && groups.length > 0 && visibleGroups.length === 0 && (
        <p>Bu kategoride çift kayıt bulunamadı.</p>
      )}
      {visibleGroups &&
        visibleGroups.map((g) => {
          const isNew = isWithinLastDay(g.latest_created_at);
          const doublePaid = g.paid_count > 1;
          return (
            <TableContainer
              key={g.users.map((u) => u.id).join("-")}
              component={Paper}
              style={{
                marginBottom: 20,
                borderLeft: doublePaid
                  ? "6px solid red"
                  : isNew
                  ? "6px solid orange"
                  : undefined,
              }}
            >
              <div
                style={{ padding: "8px 16px", fontWeight: "bold", color: "black" }}
              >
                {g.name} ·{" "}
                {g.users[0].school_name} · {g.users[0].class_id}. sınıf
                {g.match_types && g.match_types.includes("phone") && (
                  <span style={{ color: "#1976d2" }}>
                    {" "}
                    · aynı telefon (sınıf/okul farklı olabilir)
                  </span>
                )}
                {g.match_types && g.match_types.includes("grade_up") && (
                  <span style={{ color: "#1976d2" }}>
                    {" "}
                    · sınıf atlamış olabilir (aynı okul, önceki sezon hesabı)
                  </span>
                )}
                {g.match_types && g.match_types.includes("school") && (
                  <span style={{ color: "#1976d2" }}>
                    {" "}
                    · aynı okul, sınıf farklı (farklı öğrenci de olabilir;
                    ödeme otomatik kapanmaz)
                  </span>
                )}
                {doublePaid && (
                  <span style={{ color: "red" }}>
                    {" "}
                    · {g.paid_count} hesap ödenmiş, iade gerekebilir
                  </span>
                )}
                {isNew && <span style={{ color: "orange" }}> · YENİ</span>}
              </div>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={cellStyle}>ID</TableCell>
                    <TableCell style={cellStyle}>Ad Soyad</TableCell>
                    <TableCell style={cellStyle}>E-posta</TableCell>
                    <TableCell style={cellStyle}>Telefon</TableCell>
                    <TableCell style={cellStyle}>Doğum Tarihi</TableCell>
                    <TableCell style={cellStyle}>Okul</TableCell>
                    <TableCell style={cellStyle}>Sınıf</TableCell>
                    <TableCell style={cellStyle}>Kayıt Tarihi</TableCell>
                    <TableCell style={cellStyle}>Bu Sezon Onay</TableCell>
                    <TableCell style={cellStyle}>Ödeme</TableCell>
                    <TableCell style={cellStyle}>Ödeme Yapabilir mi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {g.users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.id}</TableCell>
                      <TableCell
                        style={{ cursor: "pointer", color: "#1976d2" }}
                        onClick={() => openUser(u.id)}
                      >
                        {u.name}
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.phone}</TableCell>
                      <TableCell>{formatDate(u.birthdate)}</TableCell>
                      <TableCell>
                        {u.school_name} ({u.city}/{u.district})
                      </TableCell>
                      <TableCell>{u.class_id}</TableCell>
                      <TableCell>{formatDateTime(u.created_at)}</TableCell>
                      <TableCell>{formatDateTime(u.info_confirmed_at)}</TableCell>
                      <TableCell
                        style={{ color: u.is_paid ? "green" : undefined }}
                      >
                        {paymentText(u)}
                      </TableCell>
                      <TableCell
                        style={{
                          color:
                            u.payment_hold === 1 || u.payment_block
                              ? "red"
                              : undefined,
                        }}
                      >
                        {paymentStatusText(u)}
                        {!u.is_paid && (
                          <div>
                            {u.payment_hold !== 1 && (
                              <Button
                                size="small"
                                color="secondary"
                                onClick={() => setPaymentHold(u, "block")}
                              >
                                Ödemeyi kapat
                              </Button>
                            )}
                            {u.payment_hold !== 0 &&
                              (u.payment_hold === 1 || u.payment_block) && (
                                <Button
                                  size="small"
                                  color="primary"
                                  onClick={() => setPaymentHold(u, "open")}
                                >
                                  Ödemeyi aç
                                </Button>
                              )}
                            {u.payment_hold !== null &&
                              u.payment_hold !== undefined && (
                                <Button
                                  size="small"
                                  onClick={() => setPaymentHold(u, "auto")}
                                >
                                  Otomatiğe al
                                </Button>
                              )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        })}
      {selectedUser && (
        <UpdateUser
          onClose={() => {
            setSelectedUser(null);
            load();
          }}
          selectedUser={selectedUser}
        />
      )}
    </div>
  );
};

export default Duplicates;
